import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Avatar } from '../components/Avatar';
import { Skeleton } from '../components/Skeleton';
import { EmptyState } from '../components/EmptyState';
import { fetchProfileByHandle, fetchProfileById } from '../lib/queries';
import { resolveSocialEntries } from '../lib/socials';
import { countryName } from '../lib/countries';
import { FlagIcon } from '../components/FlagIcon';
import { ProfileStripeLogo } from './Profile';
import type { Profile } from '../lib/types';
import './Profile.css';

/**
 * Public view of another member's profile (/u/:handle). Mirrors the owner's own
 * read view (Profile.tsx, .profile--me) — stripe + identity card with avatar,
 * name, @handle, badges, location, socials, bio — minus everything private or
 * owner-only: no stats, no recent posts, no banner, no edit/billing/sign-out.
 */
export default function PublicProfile() {
  const { handle = '' } = useParams();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        // Accept a user UUID as a fallback path segment, so admin-links work
        // even before the user sets a handle.
        const looksLikeUuid = /^[0-9a-f-]{8,}$/i.test(handle) && handle.length > 20;
        const p = looksLikeUuid
          ? await fetchProfileById(handle, ctrl.signal)
          : await fetchProfileByHandle(handle, ctrl.signal);
        if (ctrl.signal.aborted) return;
        if (!p) { setNotFound(true); return; }
        setProfile(p);
      } catch (err: any) {
        if (ctrl.signal.aborted || err?.name === 'AbortError') return;
        console.error('[public-profile] failed', err);
        setNotFound(true);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [handle]);

  if (loading) {
    return (
      <div className="profile profile--me" aria-busy="true" aria-label="Loading profile">
        <aside className="profile__stripe" aria-hidden="true">
          <ProfileStripeLogo />
        </aside>
        <main className="profile__pane">
          <div className="profile__pane-inner">
            <div className="profile__identity">
              <Skeleton variant="avatar" width={112} height={112} />
              <div style={{ height: 'var(--space-4)' }} />
              <Skeleton variant="text" width={220} height={28} />
              <div style={{ height: 'var(--space-2)' }} />
              <Skeleton variant="text" width="40%" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="container">
        <EmptyState
          eyebrow="Not found"
          headline="No one here by that handle."
          body="They may not have signed up yet, or the link might be off."
          cta={<Link to="/" className="btn btn--secondary btn--sm">← Back to the feed</Link>}
        />
      </div>
    );
  }

  const founding = profile.is_founding_member ?? false;
  const socials = resolveSocialEntries(profile.social_links, profile.instagram_handle);
  const locationText = [profile.location, countryName(profile.country)].filter(Boolean).join(', ');

  return (
    <div className="profile profile--me">
      <aside className="profile__stripe" aria-hidden="true">
        <ProfileStripeLogo />
      </aside>

      <main className="profile__pane">
        <div className="profile__pane-inner">
          <p className="kicker profile__eyebrow">Profile</p>

          <section className="profile__identity">
            <div className="profile__identity-head">
              <Avatar
                name={profile.display_name || 'Member'}
                src={profile.avatar_url}
                size="lg"
                founding={founding}
              />
              <div className="profile__identity-text">
                <h1 className="profile__name">{profile.display_name || 'Member'}</h1>
                {profile.handle && <p className="profile__handle">@{profile.handle}</p>}
                {(founding || profile.role !== 'user') && (
                  <div className="profile__tags">
                    {founding && <span className="profile__tag">Founding Member</span>}
                    {profile.role !== 'user' && (
                      <span className="profile__tag profile__tag--role">{profile.role}</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {locationText && (
              <dl className="profile__meta">
                <div className="profile__meta-row">
                  <dt className="profile__meta-label">Location</dt>
                  <dd className="profile__meta-value">
                    <FlagIcon code={profile.country} /> {locationText}
                  </dd>
                </div>
              </dl>
            )}

            {socials.length > 0 && (
              <div className="profile__section">
                <p className="kicker">Social profiles</p>
                <ul className="profile__socials" aria-label="Social profiles">
                  {socials.map(({ platform, value }) => (
                    <li key={platform.id}>
                      <a
                        className="profile__social"
                        href={platform.urlFor(value)}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={platform.label}
                        title={platform.label}
                      >
                        <span className="profile__social-box"><platform.Icon /></span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {profile.bio && <p className="profile__bio">{profile.bio}</p>}
          </section>
        </div>
      </main>
    </div>
  );
}
